import React from "react";
import { Button, Col, Modal, Row, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import {
  setSelectedDocumentId,
  updateMetadataField,
  setGridApi,
} from "../../../../redux/reducers/TransactionManager";
import { isNullOrUndefined } from "../../../../utils/utils";
import { ReconciledNo, ReconciledYes } from "../ReconciledBadge";
import { MetadataField } from "../../../../docuclipper/DocuclipperTypes";
import AccountSelector from "./AccountSelector";
import PeriodSelector from "./PeriodSelector";
import SelectWithBadge from "./SelectWithBadge";
import { getDocumentName } from "../../Analyze/AnalyzeTransactionsTable/utils";
import {
  EditableFieldAmount,
  EditableFieldDateYYYYMMDD,
} from "../../../../utils/EditableField";
import DocumentSelector from "./DocumentSelector";
import { Job } from "src/docuclipper/DocuclipperTypes";
// import {
//   getDailyBalanceReconciliation,
//   getDailyBalanceReconciliationTagLevel,
// } from "src/docuclipper/api";
import { useL1L2Params } from "src/hooks/useL1L2Params";

type SummaryInfo = {
  numCredits: number;
  numPayments: number;
  totalCredits: string;
  totalPayments: string;
};
export const TransactionRow = ({
  summaryInfo,
}: {
  summaryInfo: SummaryInfo;
  fieldName: string;
}) => {
  const total =
    Math.abs(parseFloat(summaryInfo.totalCredits)) -
    Math.abs(parseFloat(summaryInfo.totalPayments));

  return (
    <div className="tx-summary">
      <div className="tx-summary__header">
        <span>{`${summaryInfo.numCredits + summaryInfo.numPayments} transaction(s)`}</span>
        <span>{total.toFixed(2)}</span>
      </div>
      <div className="tx-summary__stats">
        {summaryInfo.numCredits >= 0 && (
          <span className="text-success font-weight-bold">
            {`${summaryInfo.numCredits} credit(s) +${parseFloat(summaryInfo.totalCredits.toString()).toFixed(2)}`}
          </span>
        )}
        {summaryInfo.numPayments >= 0 && (
          <span className="text-danger font-weight-bold">
            {`${summaryInfo.numPayments} debit(s) ${parseFloat(summaryInfo.totalPayments.toString()).toFixed(2)}`}
          </span>
        )}
      </div>
    </div>
  );
};

export const getIsReconciled = (
  fields: MetadataField[],
  accountName: string,
  chunk: string | null,
) => {
  const x = (fields || []).filter(
    (mf) =>
      mf.name === "isReconciled" &&
      (!chunk || mf.chunk === chunk) &&
      (accountName.toString() === "null" || mf.accountName === accountName),
  );
  if (x.length > 0) {
    const hasOne = x.filter((y) => y.value === "1");
    if (hasOne.length === x.length) {
      return <ReconciledYes type={chunk ? "period" : "account"} />;
    } else {
      return <ReconciledNo type={chunk ? "period" : "account"} />;
    }
  } else {
    return null;
  }
};

export const getIsReconciledBoolean = (
  fields: MetadataField[],
  accountName: string,
  chunk: string | null,
) => {
  const x = (fields || []).filter(
    (mf) =>
      mf.name === "isReconciled" &&
      (!chunk || mf.chunk === chunk) &&
      (accountName.toString() === "null" || mf.accountName === accountName),
  );
  if (x.length > 0) {
    const hasOne = x.filter((y) => y.value === "1");
    if (hasOne.length === x.length) {
      return true;
    } else {
      return false;
    }
  } else {
    return null;
  }
};

export const getIsBadDateBoolean = (
  fields: MetadataField[],
  accountName: string,
  chunk: string | null,
) => {
  const x = (fields || []).filter(
    (mf) =>
      ["startDate", "endDate"].includes(mf.name) &&
      (!chunk || mf.chunk === chunk) &&
      (accountName.toString() === "null" || mf.accountName === accountName),
  );
  if (x.length > 0) {
    const badDates = x.filter((y) => y.value.includes("2001"));
    return badDates.length > 0;
  } else {
    return null;
  }
};

// Which slice of the original block to render.
//   "selectors"     — all 3 dropdowns in one full-width row (legacy)
//   "document"      — DocumentSelector only (sits above PDF pane)
//   "account-period"— AccountSelector + PeriodSelector (sits above table)
//   "balances"      — balance strip (sits above table, below account-period)
//   "both"          — everything stacked (legacy)
type Section = "selectors" | "document" | "account-period" | "balances" | "both";

export const SummaryTableAccountViewHorizontal2: React.FC<{
  section?: Section;
}> = ({ section = "both" }) => {
  const dispatch = useDispatch();
  let { tls, mfs, selectedDocumentId, selectedChunk, badDates, reconciled } =
    useSelector((state: ReduxState) => state.TransactionManager);

  const gridApi = useSelector(
    (state: ReduxState) => state.TransactionManager.gridApi,
  );

  selectedChunk = selectedChunk || "";
  let { selectedAccount } = useSelector(
    (state: ReduxState) => state.TransactionManager,
  );

  selectedAccount = selectedAccount || "";

  const { mfs: fields } = mfs;
  const job = useSelector((state: ReduxState) => state.JobData.job) as Job;
  const [dailyRows, setDailyRows] = React.useState<any[]>([]);
  const [showDailyModal, setShowDailyModal] = React.useState(false);
  const [dailyLoading, setDailyLoading] = React.useState(false);
  const [dailyError, setDailyError] = React.useState<string | null>(null);
  const { totalsByDocumentId, totalsByChunk } = useSelector(
    (state: ReduxState) => state.Reconciler,
  );

  const totals = !isNullOrUndefined(selectedDocumentId)
    ? totalsByDocumentId
      ? totalsByDocumentId[selectedDocumentId as string]
      : {}
    : {};

  const chunkTotals =
    totalsByChunk &&
    !isNullOrUndefined(selectedAccount) &&
    !isNullOrUndefined(selectedAccount) &&
    !isNullOrUndefined(selectedChunk) &&
    selectedAccount in totalsByChunk &&
    !isNullOrUndefined(selectedChunk) &&
    selectedDocumentId &&
    selectedDocumentId in totalsByChunk[selectedAccount] &&
    selectedChunk in totalsByChunk[selectedAccount][selectedDocumentId]
      ? totalsByChunk[selectedAccount][selectedDocumentId][
          selectedChunk as string
        ]
      : {};

  const mfHelper = (name, accountName, documentId, chunk) => {
    const x = (fields || []).filter(
      (mf) =>
        mf.name === name &&
        mf.documentId === documentId &&
        (!selectedChunk || mf.chunk === chunk) &&
        (accountName.toString() === "null" || mf.accountName === accountName),
    );

    const getLabel = (name) => {
      switch (name) {
        case "startDate":
          return "Start Date";
        case "endDate":
          return "End Date";
        case "startBalance":
          return "Start Balance";
        case "endBalance":
          return "End Balance";
        default:
          return name;
      }
    };
    let value = "";
    if (x.length > 0) {
      value = x[0].value;
    }

    let formattedValue = value;
    if (["startBalance", "endBalance"].includes(name)) {
      if (!isNaN(parseFloat(value))) {
        formattedValue = parseFloat(value).toFixed(2);
      }
    }

    return (
      <div className="mx-2">
        {["startDate", "endDate"].includes(name) && (
          <EditableFieldDateYYYYMMDD
            onSave={(newValue) => {
              dispatch(
                updateMetadataField(
                  selectedDocumentId as any,
                  name,
                  newValue,
                  accountName,
                  chunk,
                  gridApi,
                ),
              );
            }}
            value={formattedValue}
          />
        )}
        {["startBalance", "endBalance"].includes(name) && (
          <EditableFieldAmount
            onSave={(newValue) => {
              dispatch(
                updateMetadataField(
                  selectedDocumentId as any,
                  name,
                  newValue,
                  accountName,
                  chunk,
                ),
              );
            }}
            value={formattedValue}
          />
        )}

        {!formattedValue && (
          <small>
            <div className="text-danger">{`Missing ${getLabel(name)}`}</div>
          </small>
        )}
        {formattedValue &&
          ["startDate", "endDate"].includes(name) &&
          formattedValue.includes("2001") && (
            <small>
              <div className="text-danger">{`Bad year`}</div>
            </small>
          )}
      </div>
    );
  };

  const { data: jobDocs } = useSelector(
    (state: ReduxState) => state.JobDocument,
  );
  const { l1, l2 } = useL1L2Params();
  const uniqueDocumentIds = Array.from(
    new Set((jobDocs || []).map((item) => item.documentId)),
  );

  React.useEffect(() => {
    const isTagLevel = !!(l1 && l2);
    if (
      (!isTagLevel && !job?.id) ||
      !selectedDocumentId ||
      !selectedAccount ||
      !selectedChunk
    ) {
      setDailyRows([]);
      setDailyError(null);
      return;
    }
    setDailyRows([]);
    setDailyError(null);
    setDailyLoading(false);
    // const fn = isTagLevel
    //   ? getDailyBalanceReconciliationTagLevel(
    //       l1,
    //       l2,
    //       selectedDocumentId,
    //       selectedAccount,
    //       selectedChunk,
    //     )
    //   : getDailyBalanceReconciliation(
    //       job.id,
    //       selectedDocumentId,
    //       selectedAccount,
    //       selectedChunk,
    //     );
    // fn.then((rsp) => setDailyRows(rsp?.rows || []))
    //   .catch(() => {
    //     setDailyRows([]);
    //     setDailyError("Failed to load daily reconciliation");
    //   })
    //   .finally(() => setDailyLoading(false));
  }, [job?.id, selectedDocumentId, selectedAccount, selectedChunk, l1, l2]);

  const fmtMoney = (value) => {
    const n = Number(value);
    return Number.isFinite(n)
      ? n.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";
  };
  // if (isNullOrUndefined(totals)) {
  //   return null;
  // }

  // The component renders one or both of two sections, controlled by `section`:
  //   - "selectors": the 3 dropdowns (Document / Account / Period). Lives at
  //     full width above the Split layout.
  //   - "balances": the From / Start Balance / totals / To / End Balance row.
  //     Lives inside the right Split panel, above the transactions table.
  //   - "both" (legacy): renders both stacked, the original layout.
  return (
    <>
      {(section === "selectors" || section === "both") && (
        <Row className="mt-4">
          <Col md="4" className="transation-manager-select-document-id">
            <DocumentSelector />
          </Col>
          <Col md="4" className="tx-manager-account-selector">
            <AccountSelector
              showAccountsHeading={true}
              renderReconciled={() => (
                <>
                  {selectedAccount &&
                    getIsReconciled(fields, selectedAccount, selectedChunk)}
                </>
              )}
            />
          </Col>
          <Col md="4" className="tx-manager-period-selector">
            {selectedAccount && (
              <>
                <PeriodSelector
                  renderReconciled={() => (
                    <>
                      {getIsReconciled(fields, selectedAccount, selectedChunk)}
                    </>
                  )}
                />
              </>
            )}
          </Col>
        </Row>
      )}
      {section === "document" && (
        <div className="transation-manager-select-document-id pt-2 pb-2">
          <DocumentSelector />
        </div>
      )}
      {section === "account-period" && (
        <Row className="mt-3 mb-1">
          <Col md="6" className="tx-manager-account-selector">
            <AccountSelector
              showAccountsHeading={true}
              renderReconciled={() => (
                <>
                  {selectedAccount &&
                    getIsReconciled(fields, selectedAccount, selectedChunk)}
                </>
              )}
            />
          </Col>
          <Col md="6" className="tx-manager-period-selector">
            {selectedAccount && (
              <PeriodSelector
                renderReconciled={() => (
                  <>
                    {getIsReconciled(fields, selectedAccount, selectedChunk)}
                  </>
                )}
              />
            )}
          </Col>
        </Row>
      )}
      {(section === "balances" || section === "both") && (
        <div
          className="balance-summary summary-table-dates-balances mb-2 mt-1"
          // Shift left so the From/Start Balance/To/End Balance strip
          // aligns with the AG Grid below, which sits inside a Row whose
          // -15px gutter pulls it left of the surrounding container.
          style={{ marginLeft: "-15px", marginRight: "-15px" }}
        >
          {/*
            Horizontal balance strip. Each cell is a label + value pair with
            consistent typography so the row reads as a single status line. The
            old layout used 3 vertical sections that wrapped awkwardly when the
            right Split panel was narrow; this flexbox grid wraps cell-by-cell.
          */}
          <div className="balance-summary__cells">
            <div className="balance-summary__cell">
              <span className="balance-summary__label">From</span>
              <span className="balance-summary__value">
                {selectedAccount &&
                  selectedChunk &&
                  mfHelper(
                    "startDate",
                    selectedAccount,
                    selectedDocumentId,
                    selectedChunk,
                  )}
              </span>
            </div>
            <div className="balance-summary__cell">
              <span className="balance-summary__label">Start Balance</span>
              <span className="balance-summary__value">
                {selectedAccount &&
                  selectedChunk &&
                  mfHelper(
                    "startBalance",
                    selectedAccount,
                    selectedDocumentId,
                    selectedChunk,
                  )}
              </span>
            </div>
            <div className="balance-summary__cell balance-summary__cell--totals">
              {selectedAccount &&
                selectedChunk &&
                Object.keys(chunkTotals || []).map((fieldName, idx) => (
                  <React.Fragment key={idx}>
                    {!["mfs", "label"].includes(fieldName) && (
                      <TransactionRow
                        fieldName={fieldName}
                        summaryInfo={chunkTotals[fieldName]}
                      />
                    )}
                  </React.Fragment>
                ))}
            </div>
            <div className="balance-summary__cell">
              <span className="balance-summary__label">To</span>
              <span className="balance-summary__value">
                {selectedAccount &&
                  selectedChunk &&
                  mfHelper(
                    "endDate",
                    selectedAccount,
                    selectedDocumentId,
                    selectedChunk,
                  )}
              </span>
            </div>
            <div className="balance-summary__cell">
              <span className="balance-summary__label">End Balance</span>
              <span className="balance-summary__value">
                {selectedAccount &&
                  selectedChunk &&
                  mfHelper(
                    "endBalance",
                    selectedAccount,
                    selectedDocumentId,
                    selectedChunk,
                  )}
                {(dailyRows || []).length > 0 && (
                  <Button
                    variant="link"
                    className="p-0 ml-2"
                    onClick={() => setShowDailyModal(true)}
                  >
                    Daily Balances
                  </Button>
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      <Modal
        show={showDailyModal}
        onHide={() => setShowDailyModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Daily Balance Check</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ paddingLeft: 20, paddingRight: 20 }}>
          {dailyLoading && <small className="text-muted">Loading...</small>}
          {dailyError && <small className="text-danger">{dailyError}</small>}
          {!dailyLoading && !dailyError && (
            <div
              style={{
                maxHeight: "65vh",
                overflowY: "auto",
                overflowX: "auto",
                paddingLeft: 4,
                paddingRight: 4,
              }}
            >
              <Row
                className="mb-2 mx-0 align-items-center"
                style={{ flexWrap: "wrap", rowGap: 6 }}
              >
                <span
                  className="mr-3 text-muted"
                  style={{ whiteSpace: "nowrap" }}
                >
                  Days Checked: {(dailyRows || []).length}
                </span>
                <span
                  className="mr-3 text-info"
                  style={{ whiteSpace: "nowrap" }}
                >
                  Reconciled Days:{" "}
                  {(dailyRows || []).filter((x) => x.isMatch).length}
                </span>
                <span
                  className="text-secondary"
                  style={{ whiteSpace: "nowrap" }}
                >
                  Unreconciled Days:{" "}
                  {(dailyRows || []).filter((x) => !x.isMatch).length}
                </span>
              </Row>
              <Table
                size="sm"
                striped
                bordered
                hover
                style={{
                  background: "white",
                  borderRadius: 6,
                  marginBottom: 0,
                }}
              >
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 1,
                  }}
                >
                  <tr>
                    <th>From Date</th>
                    <th className="text-right">Starting Balance</th>
                    <th>To Date</th>
                    <th className="text-right">Ending Balance</th>
                    <th className="text-right">Transactions</th>
                    <th className="text-right">Net Change</th>
                    <th className="text-right">Calculated Ending Balance</th>
                    <th className="text-right">Difference</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(dailyRows || []).map((row) => (
                    <tr key={row.id}>
                      <td>{(row.startDate || "").toString().slice(0, 10)}</td>
                      <td className="text-right">
                        {fmtMoney(row.startBalance)}
                      </td>
                      <td>{(row.endDate || "").toString().slice(0, 10)}</td>
                      <td className="text-right">{fmtMoney(row.endBalance)}</td>
                      <td className="text-right">{row.txCount}</td>
                      <td className="text-right">{fmtMoney(row.netAmount)}</td>
                      <td className="text-right">
                        {fmtMoney(row.expectedEndBalance)}
                      </td>
                      <td
                        className={`text-right ${
                          row.isMatch
                            ? "text-muted"
                            : "text-danger font-weight-bold"
                        }`}
                      >
                        {fmtMoney(row.delta)}
                      </td>
                      <td>
                        {row.isMatch ? (
                          <ReconciledYes type="period" disableOverlay={true} />
                        ) : (
                          <ReconciledNo type="period" disableOverlay={true} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};
export default SummaryTableAccountViewHorizontal2;
